import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import {usePagedList, type PagedListParams} from '@/hooks/common/use-paged-list'
import type {components} from '@/lib/api-schema'

export type Tag = components['schemas']['TagRead']
export type TagKind = 'areas' | 'positions' | 'sedes'

// R6: ninguna página pasa de 50.
const TAGS_OPCIONES_PAGE_SIZE = 50

/** Opciones de combobox: `search` filtra en el servidor; sin él llegan solo las primeras 50. */
export function useTags(businessId: string | undefined, kind: TagKind, enabled = true, search = '') {
  return useQuery({
    queryKey: ['tags', businessId, kind, search],
    queryFn: async () =>
      (
        await api.get<Page<Tag>>(`/businesses/${businessId}/${kind}`, {
          params: {page_size: TAGS_OPCIONES_PAGE_SIZE, search: search || undefined},
        })
      ).data,
    enabled: !!businessId && enabled,
  })
}

const TAGS_PAGE_SIZE = 20

export function useTagsPage(businessId: string | undefined, kind: TagKind, params: PagedListParams, enabled = true) {
  return usePagedList<Tag>(
    // Cuelga de ['tags', businessId, kind] a propósito: las invalidaciones por prefijo
    // de ['tags', …] cubren también esta lista.
    ['tags', businessId, kind, 'page'],
    businessId && enabled ? `/businesses/${businessId}/${kind}` : undefined,
    params,
    TAGS_PAGE_SIZE
  )
}

// Los empleados y las sugerencias de duplicados llevan los nombres vivos de las etiquetas.
function useTagInvalidation(businessId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({queryKey: ['tags', businessId]})
    queryClient.invalidateQueries({queryKey: ['tag-duplicates', businessId]})
    queryClient.invalidateQueries({queryKey: ['employees', businessId]})
  }
}

export type TagDuplicatePair = components['schemas']['TagDuplicatePair']

export function useDuplicateTags(businessId: string | undefined, kind: TagKind, enabled = true) {
  return useQuery({
    queryKey: ['tag-duplicates', businessId, kind],
    queryFn: async () => (await api.get<TagDuplicatePair[]>(`/businesses/${businessId}/${kind}/duplicados`)).data,
    enabled: !!businessId && enabled,
  })
}

export function useRenameTag(businessId: string, kind: TagKind) {
  const invalidate = useTagInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: {id: string; name: string}) =>
      (await api.patch<Tag>(`/businesses/${businessId}/${kind}/${input.id}`, {name: input.name})).data,
    onSuccess: invalidate,
  })
}

export function useCreateTag(businessId: string, kind: 'areas' | 'positions') {
  const invalidate = useTagInvalidation(businessId)
  return useMutation({
    mutationFn: async (name: string) => (await api.post<Tag>(`/businesses/${businessId}/${kind}`, {name})).data,
    onSuccess: invalidate,
  })
}

export function useDeleteTag(businessId: string, kind: TagKind) {
  const invalidate = useTagInvalidation(businessId)
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/businesses/${businessId}/${kind}/${id}`),
    onSuccess: invalidate,
  })
}

export function useMergeTags(businessId: string, kind: TagKind) {
  const invalidate = useTagInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: {survivorId: string; loserIds: string[]}) =>
      (
        await api.post<Tag>(`/businesses/${businessId}/${kind}/merge`, {
          survivor_id: input.survivorId,
          loser_ids: input.loserIds,
        })
      ).data,
    onSuccess: invalidate,
  })
}

// Con `q` vacío el backend devuelve el catálogo entero (TAG_SELECT_MAX_RESULTS).
export type TagSearchKind = 'area' | 'position' | 'sede'
export type TagSearchResult = components['schemas']['TagSearchResult']

export function useTagCatalog(businessId: string | undefined, kind: TagSearchKind, enabled = true) {
  return useQuery({
    queryKey: ['tag-search', businessId, kind, ''],
    queryFn: async () =>
      (await api.get<TagSearchResult[]>(`/businesses/${businessId}/tag-search`, {params: {kind, q: ''}})).data,
    enabled: !!businessId && enabled,
  })
}

type PositionDescripcion = components['schemas']['PositionDescripcionRead']

export function usePositionDescripcion(businessId: string | undefined, positionId: string | undefined) {
  return useQuery({
    queryKey: ['position-descripcion', businessId, positionId],
    queryFn: async () =>
      (await api.get<PositionDescripcion>(`/businesses/${businessId}/positions/${positionId}/descripcion`)).data,
    enabled: !!businessId && !!positionId,
  })
}

export type TagPerson = components['schemas']['TagPersonRead']

const TAG_PEOPLE_PAGE_SIZE = 20

export function useTagPeople(
  businessId: string | undefined,
  kind: TagKind,
  tagId: string | undefined,
  page = 1,
  enabled = true
) {
  return useQuery({
    queryKey: ['tag-people', businessId, kind, tagId, page],
    queryFn: async () =>
      (
        await api.get<Page<TagPerson>>(`/businesses/${businessId}/${kind}/${tagId}/people`, {
          params: {page, page_size: TAG_PEOPLE_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId && !!tagId && enabled,
    placeholderData: (previous) => previous,
  })
}

type PositionDetail = components['schemas']['PositionDetailRead']

export function usePositionDetail(businessId: string | undefined, positionId: string | undefined) {
  return useQuery({
    queryKey: ['position-detail', businessId, positionId],
    queryFn: async () =>
      (await api.get<PositionDetail>(`/businesses/${businessId}/positions/${positionId}/detail`)).data,
    enabled: !!businessId && !!positionId,
  })
}

/** Solo el dueño; la consolidación automática nunca la sobreescribe (guarda del backend). */
export function useSetOfficialDescripcion(businessId: string, positionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (officialDescripcion: string | null) =>
      (
        await api.patch<PositionDetail>(`/businesses/${businessId}/positions/${positionId}/descripcion-oficial`, {
          official_descripcion: officialDescripcion,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['position-detail', businessId, positionId]})
      queryClient.invalidateQueries({queryKey: ['position-descripcion', businessId, positionId]})
    },
  })
}

export type SedeDetail = components['schemas']['BusinessSedeRead']
export type SedeDetailFields = components['schemas']['SedeDetailFields']
export type SedeCreate = components['schemas']['SedeCreate']

export function useSedeDetail(businessId: string | undefined, sedeId: string | undefined) {
  return useQuery({
    queryKey: ['sede-detail', businessId, sedeId],
    queryFn: async () => (await api.get<SedeDetail>(`/businesses/${businessId}/sedes/${sedeId}/detalle`)).data,
    enabled: !!businessId && !!sedeId,
  })
}

function useSedeInvalidation(businessId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({queryKey: ['tags', businessId, 'sedes']})
    queryClient.invalidateQueries({queryKey: ['tag-duplicates', businessId, 'sedes']})
    queryClient.invalidateQueries({queryKey: ['sede-detail', businessId]})
    queryClient.invalidateQueries({queryKey: ['employees', businessId]})
  }
}

export function useCreateSede(businessId: string) {
  const invalidate = useSedeInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: SedeCreate) =>
      (await api.post<SedeDetail>(`/businesses/${businessId}/sedes`, input)).data,
    onSuccess: invalidate,
  })
}

export function useUpdateSedeDetail(businessId: string, sedeId: string) {
  const invalidate = useSedeInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: SedeDetailFields) =>
      (await api.patch<SedeDetail>(`/businesses/${businessId}/sedes/${sedeId}/detalle`, input)).data,
    onSuccess: invalidate,
  })
}
